/**
 * SprechenSamplesCallout — in-text link from a Redemittel post or exam guide to the
 * matching "<exam> Sprechen: 3 sample exams" post.
 *
 * Why (SEO): the nine Sprechen sample posts launched with no links from the posts that
 * already rank. A descriptive, in-content anchor carries more weight than the footer
 * block alone, and it is the link a reader of the speaking section actually wants.
 * It is a real <a href> (react-router <Link>), so it is in the prerendered HTML.
 *
 * The blog is English-only, so the copy is hardcoded like the rest of the post bodies.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { MessagesSquare } from 'lucide-react';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

const PROVIDER_LABEL = { telc: 'telc', goethe: 'Goethe', osd: 'ÖSD' };

/** @param {{ provider: 'telc'|'goethe'|'osd', level: 'b1'|'b2'|'c1' }} props */
export default function SprechenSamplesCallout({ provider, level }) {
  const lang = useLangParam();
  const exam = `${PROVIDER_LABEL[provider] || provider} ${level.toUpperCase()}`;

  return (
    <aside
      aria-label={`${exam} Sprechen sample exams`}
      className="my-10 flex items-start gap-4 rounded-2xl border-2 border-black bg-white p-5 sm:p-6"
    >
      <span
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-black text-white"
        aria-hidden="true"
      >
        <MessagesSquare className="h-5 w-5" />
      </span>
      <div>
        <p className="font-space text-xs font-black uppercase tracking-wider text-[#3A86FF]">Hear the phrases in a real oral exam</p>
        <p className="mt-1 text-base leading-relaxed text-black/70 md:text-lg">
          See how these phrases sound in a complete speaking exam:{' '}
          <Link
            to={`/${lang}/blog/${provider}-${level}-sprechen-sample-exams`}
            className="font-bold text-black underline underline-offset-2 hover:opacity-80"
          >
            {exam} Sprechen: 3 sample exams with model dialogues
          </Link>
          . Each one has the exam sheets, a model dialogue for every part and the key phrases marked.
        </p>
      </div>
    </aside>
  );
}
